
import Link from 'next/link';
import Router from 'next/router';
import { Component, createRef, useEffect, useMemo, useRef, useState } from 'react';
import styles from './extension-select-dropdown.component.module.scss';
import { option } from '../../ui/contact-us-modal/contact-us.modal.scss';
import { createPopper } from '@popperjs/core';
import { OptionItem, ToRowData } from '../models/process-selection-row-data';

interface SearchableDropdownProps {
    id?: string;
    options: ToRowData[];
    selectedOption: ToRowData | null;
    selectedFromOption: string | null;
    formatOption: (value: ToRowData | null) => string;
    formatFromOption: (value: OptionItem | null) => string;
    onChange: (value: ToRowData, fromOption: string) => void;
}

interface SearchableDropdownState {
    isOpen: boolean;
    searchTerm: string;
    filteredOptions: ToRowData[];
    selectedOption: ToRowData | null;
    selectedFromOption: string | null;
}

export class SearchableDropdown extends Component<SearchableDropdownProps, SearchableDropdownState> {
    // A ref to the main container to detect outside clicks
    private dropdownRef = createRef<HTMLDivElement>();
    private dropdownMenuRef = createRef<HTMLDivElement>();

    constructor(props: SearchableDropdownProps) {
        super(props);

        // Initialize state
        this.state = {
            isOpen: false,
            searchTerm: '',
            selectedOption: props.selectedOption,
            selectedFromOption: props.selectedFromOption,
            filteredOptions: null,
        };
    }

    // Add event listener when the component mounts
    componentDidMount() {
        document.addEventListener('mousedown', this.handleClickOutside);
    }

    // Clean up the event listener when the component unmounts
    componentWillUnmount() {
        document.removeEventListener('mousedown', this.handleClickOutside);
    }

    // Method to close dropdown if a click occurs outside of it
    handleClickOutside = (event: MouseEvent) => {
        if (this.dropdownRef.current && !this.dropdownRef.current.contains(event.target as Node)) {
            this.setState({
                isOpen: false,
                searchTerm: '', // Reset search on close
                selectedOption: this.props.selectedOption, // Reset to original
                selectedFromOption: this.props.selectedFromOption, // Reset to original
            });
        }
    };

    // Method to handle selecting an option
    handleSelectOption = (group: ToRowData, option: string) => {
        // Fix: Find the original group from props to ensure we don't pass back a filtered version (subset of options)
        const originalGroup = this.props.options.find(g => g.mediaName === group?.mediaName) ?? group;

        this.props.onChange(originalGroup, option);
        this.setState({
            isOpen: false,
            searchTerm: '', // Reset search on select
            filteredOptions: null,
            selectedOption: originalGroup // Update selected option to the new group
        });
    };

    // Method to toggle the dropdown's visibility
    toggleDropdown = () => {
        this.setState((prevState) => ({
            isOpen: !prevState.isOpen,
            searchTerm: '',
            selectedOption: this.props.selectedOption,
            selectedFromOption: this.props.selectedFromOption,
            filteredOptions: null,
        }));
    };

    componentDidUpdate(prevProps: SearchableDropdownProps, prevState: SearchableDropdownState) {
        if (this.state.isOpen && !prevState.isOpen && this.props.selectedFromOption) {
            const id = `ext-option-${this.props.selectedFromOption}`;
            const el = document.getElementById(id);
            if (el) {
                el.scrollIntoView({ block: 'nearest' });
            }
        }
    }

    // // Set the default active category when the dropdown opens or search term changes
    // setDefaultActiveCategory = () => {
    //     const filteredData = this.getFilteredData();
    //     const isCategoryVisible = filteredData.some(g => g.category === this.state.activeCategory);

    //     if (!isCategoryVisible && filteredData.length > 0) {
    //         this.setState({ activeCategory: filteredData[0].category });
    //     } else if (this.state.isOpen && !this.state.activeCategory && filteredData.length > 0) {
    //         this.setState({ activeCategory: filteredData[0].category });
    //     }
    // };

    // Handle changes to the search input
    handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newState = {
            searchTerm: e.target.value,
            ...this.getFilteredDataState(e.target.value),
        }
        this.setState(newState);
    };

    // Memoization-like getter to filter data based on the search term
    getFilteredDataState = (searchTerm: string) => {
        const { options } = this.props;

        if (!searchTerm || searchTerm.trim() === '') {
            return {
                filteredOptions: null,
                selectedOption: this.props.selectedOption,
                selectedFromOption: this.props.selectedFromOption,
            }
        }
        const filter = searchTerm.toUpperCase();

        const filteredOptions: ToRowData[] = []
        let isSelectedOptionAndValuePartOfFilteredOptions = false;
        options.forEach(option => {
            const filteredFromOptions = option.options.filter(a => a.value.includes(filter) || a.name.toUpperCase().includes(filter));
            if (filteredFromOptions.length > 0) {
                filteredOptions.push({
                    ...option,
                    mediaType: option.mediaType,
                    mediaName: option.mediaName,
                    options: filteredFromOptions,
                })
                isSelectedOptionAndValuePartOfFilteredOptions = filteredFromOptions.some(item => item.value === this.state.selectedFromOption)

            }
        });

        // if (isSelectedOptionAndValuePartOfFilteredOptions) {
        //     return {
        //         filteredOptions: filteredOptions,
        //         selectedOption: this.state.selectedOption,
        //         selectedFromOption: this.state.selectedFromOption,
        //     }
        // }

        return {
            filteredOptions: filteredOptions,
            selectedOption: filteredOptions.length > 0 ? filteredOptions[0] : null,
            selectedFromOption: filteredOptions.length > 0 ? filteredOptions[0].options[0].value : null,
        };
    };

    // Clear search term
    clearSearch = () => {
        this.setState({
            searchTerm: '',
            ...this.getFilteredDataState(''),
        });
    };

    private resolveSelectedItem(): OptionItem | null {
        const v = this.props.selectedFromOption;
        if (!v) return null;
        for (const row of this.props.options) {
            const item = row.options.find(o => o.value === v);
            if (item) return item;
        }
        return null;
    }

    render() {
        const { isOpen, searchTerm } = this.state;
        const filteredData = this.state.filteredOptions;

        return (
            <div className={`${styles["dropdown-container"]}`} ref={this.dropdownRef} id={this.props.id}>
                <button className={`${styles["dropdown-button"]}`} onClick={this.toggleDropdown}>
                    {this.props.formatFromOption(this.resolveSelectedItem())}
                </button>

                {isOpen && (
                    <div ref={this.dropdownMenuRef} className={`${styles["dropdown-menu"]}`}>
                        <div className={styles['search-wrapper']}>
                            <input
                                type="text"
                                placeholder="Search Format"
                                className={`${styles["dropdown-search"]}`}
                                value={searchTerm}
                                onChange={this.handleSearchChange}
                                autoFocus
                            />
                            {searchTerm && (
                                <button
                                    className={styles['search-clear-btn']}
                                    onClick={this.clearSearch}
                                    title="Clear search"
                                >
                                    &times;
                                </button>
                            )}
                        </div>

                        {isOpen && filteredData == null &&
                            <div className={`${styles["dropdown-body"]}`}>
                                {!searchTerm && (
                                    <ul className={`${styles["dropdown-categories"]}`}>
                                        {this.props.options.length > 0 && this.props.options.map((group) => (
                                            <li
                                                key={group.mediaType}
                                                className={group.mediaName === this.state.selectedOption?.mediaName ? styles.active : ''}
                                                onClick={() => this.setState({ selectedOption: group })}
                                            >
                                                {this.props.formatOption(group)}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                                <ul className={`${styles["dropdown-options"]}`}>
                                    {this.state.selectedOption?.options?.map((option) => (
                                        <li key={option.value} id={`ext-option-${option.value}`}>
                                            <button
                                                onClick={() => this.handleSelectOption(this.state.selectedOption!, option.value)}
                                                className={`btn ${option.value === this.state.selectedFromOption ? styles['active'] : ''}`}
                                            >
                                                {option.name}
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        }
                        {isOpen && Array.isArray(filteredData) && filteredData.length > 0 &&
                            <div className={`${styles["dropdown-body"]}`}>
                                {!searchTerm && (
                                    <ul className={`${styles["dropdown-categories"]}`}>
                                        {filteredData.map((group) => (
                                            <li
                                                key={group.mediaType}
                                                className={group.mediaName === this.state.selectedOption?.mediaName ? styles.active : ''}
                                                onClick={() => this.setState({ selectedOption: group })}
                                            >
                                                {this.props.formatOption(group)}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                                <ul className={`${styles["dropdown-options"]}`}>
                                    {searchTerm ? (
                                        filteredData.flatMap(group => group.options.map(opt => ({ group, option: opt }))).map(({ group, option }) => (
                                            <li key={`${group.mediaName}-${option.value}`} style={{ width: '100%' }}>
                                                <button
                                                    onClick={() => this.handleSelectOption(group, option.value)}
                                                    className={`btn ${option.value === this.state.selectedFromOption ? styles['active'] : ''}`}
                                                >
                                                    {option.name}
                                                </button>
                                            </li>
                                        ))
                                    ) : (
                                        this.state.selectedOption?.options?.map((option) => (
                                            <li key={option.value} id={`ext-option-${option.value}`}>
                                                <button
                                                    onClick={() => this.handleSelectOption(this.state.selectedOption!, option.value)}
                                                    className={`btn ${option.value === this.state.selectedFromOption ? styles['active'] : ''}`}
                                                >
                                                    {option.name}
                                                </button>
                                            </li>
                                        ))
                                    )}
                                </ul>
                            </div>
                        }

                        {isOpen && Array.isArray(filteredData) && filteredData.length == 0 &&
                            <div className={`${styles["dropdown-body"]}`}>
                                <ul className={`${styles["dropdown-no-options"]}`}>
                                    No results found for search term <b>{this.state.searchTerm}</b>.
                                </ul>
                            </div>
                        }
                    </div>
                )}
            </div>
        );
    }
}

